import { googleCampaignQuery, landingCampaign } from './campaign'
import { portalMode } from './portal'

/**
 * Google's tag (Google Analytics 4, which also measures Google Ads) and the Meta Pixel (Facebook
 * and Instagram ads), only after the visitor allows them (GDPR/ePrivacy). Google's tag loads with
 * consent to analytics or ad measurement, with Consent Mode v2 in its basic form: nothing loads
 * before a choice, and the consent states tell Google what was allowed. The choice is a cookie on
 * .arvaseo.com shared with the website (site/main.js), so people are asked once for both.
 *
 * Production builds get the IDs from VITE_GA_MEASUREMENT_ID, VITE_GOOGLE_ADS_SIGNUP_CONVERSION
 * and VITE_META_PIXEL_ID; with none nothing loads and no notice is shown (local development,
 * tests). Page addresses are sent without their query string or fragment, which can hold reset,
 * invitation and ticket tokens, except the first page's campaign tags and Google click IDs
 * (lib/campaign), so a visit from an ad or campaign is credited to it. Google signals and ad
 * personalisation (remarketing) are on only with consent to ad measurement.
 *
 * Google Ads has its own tag (AW-…), configured through the same gtag.js only with consent to ad
 * measurement: it reads the click ID of a visit from our ads and reports a new account as the
 * sign-up conversion action, so the campaigns can count and optimise for sign-ups themselves,
 * without waiting for an import from Google Analytics.
 *
 * The Meta Pixel reports no page views from the app: it runs only to report a new account
 * (CompleteRegistration), with consent to ad measurement, and the API reports the same sign-up
 * through Meta's Conversions API under a shared event ID, so Meta counts it once.
 */
// Never on an agency's portal address: its visitors are the agency's clients, not Arva SEO's.
export const measurementId = portalMode
  ? ''
  : (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || ''
export const metaPixelId = portalMode ? '' : (import.meta.env.VITE_META_PIXEL_ID as string | undefined) || ''
/**
 * The Google Ads sign-up conversion action as its tag's `send_to` value, "AW-<id>/<label>"
 * (Google Ads → Goals → Conversions → the action → Tag setup). Anything else is ignored.
 */
export const adsConversion = portalMode
  ? ''
  : (/^AW-\d+\/[\w-]+$/.exec(
      (import.meta.env.VITE_GOOGLE_ADS_SIGNUP_CONVERSION as string | undefined) ?? '',
    )?.[0] ?? '')
/** The Google Ads tag ID the conversion belongs to, for gtag's config. */
export const adsTagId = adsConversion.split('/')[0] ?? ''
/** Whether any tag is set up: only then is the cookie notice shown. */
export const analyticsAvailable = measurementId !== '' || adsConversion !== '' || metaPixelId !== ''

/** Each optional kind of cookie. Necessary cookies are always on. */
export interface Choice {
  analytics: boolean
  /** Google Ads and Meta: counting sign-ups from our ads, and showing our ads to past visitors. */
  ads: boolean
  referrals: boolean
}

/** The visitor's choice; null until they choose. `outdated`: chosen before ad measurement existed. */
export type Consent = (Choice & { outdated?: boolean }) | null

const CONSENT_COOKIE = 'arva_consent'
/** Ask again after about six months. */
const CONSENT_SECONDS = 182 * 24 * 60 * 60
/** Google Analytics cookies last 13 months at most. */
const GA_COOKIE_SECONDS = 395 * 24 * 60 * 60

export const COOKIE_SETTINGS_EVENT = 'arvaseo:cookie-settings'

type Gtag = (...args: unknown[]) => void

/** The Meta Pixel's `fbq`: calls queue up until its script loads and takes over through callMethod. */
interface Fbq {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: Fbq
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
    fbq?: Fbq
    _fbq?: Fbq
  }
}

/**
 * The arva_consent cookie (shared with site/main.js): "v2:" and the kinds allowed, joined by "+"
 * (v2:analytics+ads+referrals, v2:none). Older values came before ad measurement: granted and
 * only:analytics / only:referrals keep their choices but are outdated, so the notice asks again;
 * denied stays a no to everything.
 */
export function parseConsent(value: string | undefined): Consent {
  const v2 = /^v2:(none|[a-z+]+)$/.exec(value ?? '')
  if (v2?.[1]) {
    const kinds = v2[1] === 'none' ? [] : v2[1].split('+')
    return {
      analytics: kinds.includes('analytics'),
      ads: kinds.includes('ads'),
      referrals: kinds.includes('referrals'),
    }
  }
  if (value === 'denied') return { analytics: false, ads: false, referrals: false }
  if (value === 'granted') return { analytics: true, ads: false, referrals: true, outdated: true }
  const only = /^only:(analytics|referrals)$/.exec(value ?? '')
  return only
    ? { analytics: only[1] === 'analytics', ads: false, referrals: only[1] === 'referrals', outdated: true }
    : null
}

export function consentValue(choice: Choice): string {
  const kinds = (['analytics', 'ads', 'referrals'] as const).filter((kind) => choice[kind])
  return `v2:${kinds.length > 0 ? kinds.join('+') : 'none'}`
}

export function readConsent(): Consent {
  return parseConsent(/(?:^|;\s*)arva_consent=([^;]+)/.exec(document.cookie)?.[1])
}

/** The cookie belongs to .arvaseo.com in production so the website and the app share it. */
export function cookieDomain(): string {
  const host = window.location.hostname
  return host === 'arvaseo.com' || host.endsWith('.arvaseo.com') ? '; domain=.arvaseo.com' : ''
}

export function saveConsent(choice: Choice) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${CONSENT_COOKIE}=${consentValue(choice)}; Max-Age=${CONSENT_SECONDS}; path=/; ` +
    `SameSite=Lax${cookieDomain()}${secure}`
}

/** Without consent to referrals, the partner referral cookie goes too (and older ones). */
export function stopReferrals() {
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim() ?? ''
    if (!['arva_ref', 'arva_aff_ref', 'ls_aff_ref'].includes(name)) continue
    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/${cookieDomain()}`
  }
}

/** Opens the cookie notice again ("Cookie settings" in the account menu and on sign-in pages). */
export function openCookieSettings() {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))
}

/** White-label pages seen by agencies' clients, and one-off email links, never use analytics. */
export function isExcludedPath(pathname: string): boolean {
  return (
    /^\/(r\/|email\/unsubscribe|email\/open|reports\/unsubscribe|updates\/keep-running)/.test(pathname) ||
    /^\/w\/[^/]+\/portal(\/|$)/.test(pathname)
  )
}

/** An address as sent to Google: origin and path only. */
export function cleanUrl(url: string): string {
  try {
    const parsed = new URL(url)
    return parsed.origin + parsed.pathname
  } catch {
    return ''
  }
}

const disableKey = () => `ga-disable-${measurementId}`

const granted = (yes: boolean) => (yes ? 'granted' : 'denied')

/** Consent Mode v2: what the visitor allowed, in Google's terms. */
function consentState(choice: Choice) {
  return {
    analytics_storage: granted(choice.analytics),
    ad_storage: granted(choice.ads),
    ad_user_data: granted(choice.ads),
    ad_personalization: granted(choice.ads),
  }
}

/** The choice applied in this tab (loadAnalytics or stopAnalytics); null until the cookie notice has run. */
let applied: Choice | null = null
/** Whether a loaded Meta Pixel has been told to stop (consent revoked), so a later OK can let it send again. */
let pixelRevoked = false
/** Whether the Google Ads tag has been configured in this tab (it is, once, at the first OK to ad measurement). */
let adsConfigured = false

/**
 * Configures the Google Ads tag, once, with consent to ad measurement: from then on it keeps the
 * click ID of a visit from our ads (the `_gcl_aw` cookie) and can report the sign-up conversion.
 * Withdrawn consent stops it through Consent Mode (stopAnalytics also removes its cookies).
 */
function configureAds(choice: Choice) {
  if (adsConfigured || adsTagId === '' || !choice.ads || !window.gtag) return
  adsConfigured = true
  window.gtag('config', adsTagId, {
    allow_ad_personalization_signals: true,
    page_location: cleanUrl(window.location.href) + googleCampaignQuery(),
    page_referrer: cleanUrl(document.referrer),
  })
}

/**
 * Meta's click cookie (_fbc, "fb.1.<time>.<fbclid>" in Meta's documented format), created with
 * consent to ad measurement when the visit came from a Meta ad, as the Pixel does on arvaseo.com.
 * The app's Pixel only loads at a sign-up, when the address no longer has the click ID, so this
 * keeps the sign-up (the Pixel's report and the API's) credited to the ad. 90 days, like Meta's.
 */
function keepMetaClick() {
  const fbclid = landingCampaign().fbclid
  if (fbclid === null || metaPixelId === '') return
  if (/(?:^|;\s*)_fbc=([^;]+)/.exec(document.cookie)?.[1]?.endsWith(`.${fbclid}`)) return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `_fbc=fb.1.${Date.now()}.${fbclid}; Max-Age=${90 * 24 * 60 * 60}; path=/; ` +
    `SameSite=Lax${cookieDomain()}${secure}`
}

/**
 * Applies a choice that allows analytics or ad measurement: loads Google's tag once (or tells an
 * already loaded tag about a changed choice), and lets the Meta Pixel send again if a sign-up had
 * loaded it. Allowing neither stops them (see stopAnalytics).
 */
export function loadAnalytics(choice: Choice) {
  if (!analyticsAvailable || (!choice.analytics && !choice.ads)) return
  applied = choice
  if (choice.ads) keepMetaClick()
  if (choice.ads && pixelRevoked) {
    window.fbq?.('consent', 'grant')
    pixelRevoked = false
  }
  const tagId = measurementId || adsTagId
  if (tagId === '') return
  if (measurementId !== '') (window as unknown as Record<string, unknown>)[disableKey()] = false
  if (window.gtag) {
    window.gtag('consent', 'update', consentState(choice))
    window.gtag('set', { allow_google_signals: choice.ads, allow_ad_personalization_signals: choice.ads })
    configureAds(choice)
    return
  }

  const dataLayer = (window.dataLayer = window.dataLayer ?? [])
  window.gtag = function gtag() {
    // gtag.js reads the arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  window.gtag('consent', 'update', consentState(choice))
  window.gtag('js', new Date())
  if (measurementId !== '') {
    window.gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: choice.ads,
      allow_ad_personalization_signals: choice.ads,
      cookie_expires: GA_COOKIE_SECONDS,
      page_location: cleanUrl(window.location.href) + googleCampaignQuery(),
      page_referrer: cleanUrl(document.referrer),
    })
  }
  configureAds(choice)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`
  document.head.appendChild(script)
}

let previousLocation = ''

export function trackPageView(pathname: string) {
  if (!window.gtag || isExcludedPath(pathname)) return
  const location = window.location.origin + pathname
  if (location === previousLocation) return

  const referrer = previousLocation || cleanUrl(document.referrer)
  // The visit's first page keeps its campaign tags and Google click IDs (lib/campaign).
  const pageLocation = previousLocation === '' ? location + googleCampaignQuery() : location
  window.gtag('set', { page_location: pageLocation, page_referrer: referrer })
  window.gtag('event', 'page_view', {
    page_location: pageLocation,
    page_referrer: referrer,
    page_title: document.title,
  })
  previousLocation = location
}

/** Removes cookies whose name matches, on this host and on .arvaseo.com. */
function removeCookies(pattern: RegExp) {
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim() ?? ''
    if (!pattern.test(name)) continue
    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/${cookieDomain()}`
  }
}

/**
 * Applies a withdrawn choice at once: without analytics the Google Analytics cookies go, without
 * ad measurement the Google Ads and Meta ones (and the Meta Pixel, if loaded, stops), and with
 * neither nothing more is sent.
 */
export function stopAnalytics(choice: Choice = { analytics: false, ads: false, referrals: false }) {
  if (!analyticsAvailable) return
  applied = choice
  if (!choice.analytics) removeCookies(/^(_ga|_gid|_gat)/)
  if (!choice.ads) {
    // Meta's cookies are set by the Meta Pixel (on arvaseo.com, and here at a sign-up) on .arvaseo.com.
    if (window.fbq) {
      window.fbq('consent', 'revoke')
      pixelRevoked = true
    }
    removeCookies(/^(_gcl_|_gac_|_fbp$|_fbc$)/)
  }
  if (choice.analytics || choice.ads) {
    loadAnalytics(choice)
    return
  }
  if (measurementId !== '') (window as unknown as Record<string, unknown>)[disableKey()] = true
  // A loaded Google Ads tag is told too, so it sends no more than cookieless pings.
  window.gtag?.('consent', 'update', consentState(choice))
  previousLocation = ''
}

const PENDING_SIGN_UP = 'arva-pending-sign-up'

type SignUpMethod = 'email' | 'google'

interface SignUp {
  method: SignUpMethod
  eventId: string
}

/** A random ID for one sign-up: the browser's report and the API's share it, so Meta keeps one. */
export function newSignUpEventId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }
}

/**
 * What a registration request carries for ad measurement (the sign-up form, "Continue with
 * Google"): whether the visitor allows it, so the API reports the sign-up to Meta too (its
 * Conversions API), and the event ID the browser's own report shares with it. Never on an
 * agency's portal address.
 */
export function signUpTracking(): { ad_consent: boolean; ad_event_id: string } {
  return { ad_consent: !portalMode && (readConsent()?.ads ?? false), ad_event_id: newSignUpEventId() }
}

/** Loads the Meta Pixel, once, when it's set up: only ever for a sign-up, with consent to ad measurement. */
function metaPixel(): Fbq | null {
  if (metaPixelId === '') return null
  if (window.fbq) return window.fbq
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  }) as Fbq
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = fbq
  window.fbq = fbq
  window._fbq ??= fbq
  // No automatic events or page metadata from the app: it reports the sign-up and nothing else.
  fbq('set', 'autoConfig', false, metaPixelId)
  fbq('init', metaPixelId)
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
  return fbq
}

function sendSignUp({ method, eventId }: SignUp) {
  if (window.gtag && measurementId !== '') window.gtag('event', 'sign_up', { method })
  if (!applied?.ads) return
  // The event ID doubles as Google's transaction ID, so a sign-up reported twice counts once.
  if (adsConfigured && window.gtag) {
    window.gtag('event', 'conversion', { send_to: adsConversion, transaction_id: eventId })
  }
  metaPixel()?.('track', 'CompleteRegistration', {}, { eventID: eventId })
}

/**
 * A new Arva SEO account, reported as `sign_up` to Google Analytics, as a conversion to Google Ads
 * and as `CompleteRegistration` to the Meta Pixel (both only with consent to ad measurement), so
 * paid ads can count conversions.
 * Until the visitor's choice has been applied in this tab (a Google sign-up returning from Google,
 * before the cookie notice has run), it waits here and goes out with the choice (flushSignUp).
 */
export function trackSignUp(method: SignUpMethod, eventId: string = newSignUpEventId()) {
  if (applied) {
    sendSignUp({ method, eventId })
    return
  }
  try {
    sessionStorage.setItem(PENDING_SIGN_UP, JSON.stringify({ method, eventId }))
  } catch {
    // Storage blocked: the sign-up isn't counted.
  }
}

/** Sends a sign-up that happened before the visitor's choice was applied. */
export function flushSignUp() {
  if (!applied) return
  try {
    const raw = sessionStorage.getItem(PENDING_SIGN_UP)
    if (raw === null) return
    sessionStorage.removeItem(PENDING_SIGN_UP)
    const pending = parsePendingSignUp(raw)
    if (pending) sendSignUp(pending)
  } catch {
    // Storage blocked: nothing waiting.
  }
}

/** Earlier builds kept only the method; now the event ID too. */
function parsePendingSignUp(raw: string): SignUp | null {
  if (raw === 'email' || raw === 'google') return { method: raw, eventId: newSignUpEventId() }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { method, eventId } = parsed as Partial<SignUp>
    if (method !== 'email' && method !== 'google') return null
    return { method, eventId: typeof eventId === 'string' && eventId !== '' ? eventId : newSignUpEventId() }
  } catch {
    return null
  }
}

/**
 * A Google sign-up that created an account comes back with `#welcome=google&event_id=…` (the API
 * adds it, having reported the sign-up to Meta under that event ID when consent allowed).
 */
export function captureGoogleSignUp() {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  if (fragment.get('welcome') !== 'google') return
  trackSignUp('google', fragment.get('event_id') || undefined)
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
}
